// src/containers/layout/oura-shell.tsx
//
// Which chrome an Oura screen sits in.
//
// Oura staff get the Oura sidebar (OuraAuthLayout) on every staff route;
// platform admins, who also run other products, keep the platform's own
// layout with "Businesses" under Growth beside Recruitment and
// Administration. Inside one
// business, in the tab the Businesses list opened for it, the platform sidebar
// steps aside for the business sidebar (OuraBusinessShell + BusinessDetail).
//
// `oura-client` gets the merchant shell (OuraAuthLayout): the same Oura
// sidebar as the business chrome, with the merchant's five items, and a
// bottom bar on a phone.

import type { ReactNode } from "react";
import { useParams } from "react-router-dom";
import AuthLayout from "./auth";
import OuraAuthLayout from "./oura-auth";
import OuraBusinessShell from "./oura-business-shell";
import { isPlatformAdmin, useOuraRole } from "@oura/role";
import { useUserProfileContext } from "@context/user_profile";
import { isBusinessSidebarLive } from "@oura/nav";
import { ASK_OURA_ON, AskOuraMount } from "@oura/ask";

export default function OuraShell({ children }: { children: ReactNode }) {
    const role = useOuraRole();
    const { businessId } = useParams();
    const [, userInfo] = useUserProfileContext();
    // Ask Oura (staff only, behind VITE_ENABLE_OURA_ASK): one chat on top of
    // the existing staff screens. Off, nothing is added to the chrome.
    const ask = ASK_OURA_ON && role === "oura-staff" ? <AskOuraMount businessId={businessId} /> : null;

    // Drilled into one business: the business sidebar is the nav.
    if (role === "oura-staff" && businessId && isBusinessSidebarLive) {
        return (
            <OuraBusinessShell>
                {children}
                {ask}
            </OuraBusinessShell>
        );
    }

    // An Oura staff login has only Oura, so it gets Oura's own sidebar on the
    // list, Requests, Skills and the Tracker too: the same rail and logo it
    // sees inside a business, not the ourteam platform bar (Jack, 22 Oct:
    // "feels like two apps"). A platform admin also runs the other products,
    // so keeps the platform sidebar that reaches them.
    if (role === "oura-staff") {
        return isPlatformAdmin(userInfo?.role) ? (
            <AuthLayout>
                {children}
                {ask}
            </AuthLayout>
        ) : (
            <OuraAuthLayout>
                {children}
                {ask}
            </OuraAuthLayout>
        );
    }
    return <OuraAuthLayout>{children}</OuraAuthLayout>;
}
